<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Registro de Actividades</h1>
          <p class="text-purple-100 mt-1">Historial completo de actividades del sistema</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100">
            <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Actividades</p>
            <p class="text-2xl font-semibold text-gray-900">{{ filteredActivities?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Creaciones</p>
            <p class="text-2xl font-semibold text-gray-900">{{ createdCount }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Actualizaciones</p>
            <p class="text-2xl font-semibold text-gray-900">{{ updatedCount }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Eliminaciones</p>
            <p class="text-2xl font-semibold text-gray-900">{{ deletedCount }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters and Actions -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div class="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
          <!-- Entity Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Filtrar por entidad</label>
            <Dropdown
              v-model="selectedEntity"
              :options="entityOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Todas las entidades"
              showClear
              class="w-full"
              :class="'p-dropdown-custom'"
            />
          </div>
          
          <!-- Action Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Filtrar por acción</label>
            <Dropdown
              v-model="selectedAction"
              :options="actionOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Todas las acciones"
              showClear
              class="w-full"
              :class="'p-dropdown-custom'"
            />
          </div>
          
          <!-- Source Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Filtrar por fuente</label>
            <Dropdown
              v-model="selectedSource"
              :options="sourceOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Todas las fuentes"
              showClear
              class="w-full"
              :class="'p-dropdown-custom'"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        :value="filteredActivities"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay actividades</h3>
            <p class="mt-1 text-sm text-gray-500">Las actividades aparecerán aquí cuando se realicen acciones en el sistema.</p>
          </div>
        </template>
        
        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <p class="mt-2 text-gray-600">Cargando actividades...</p>
          </div>
        </template>

        <Column field="description" header="Descripción" sortable>
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full flex items-center justify-center" :class="getActivityIconClass(data.action).iconBg">
                  <svg class="h-5 w-5" :class="getActivityIconClass(data.action).iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getActivityIcon(data.action)"></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <div class="text-sm font-medium text-gray-900">{{ data.description }}</div>
                <div class="text-sm text-gray-500">{{ data.entityName }}</div>
              </div>
            </div>
          </template>
        </Column>

        <Column field="entity" header="Entidad" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                  :class="getEntityBadgeClass(data.entity)">
              {{ translateEntity(data.entity) }}
            </span>
          </template>
        </Column>

        <Column field="action" header="Acción" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                  :class="getActionBadgeClass(data.action)">
              {{ translateAction(data.action) }}
            </span>
          </template>
        </Column>

        <Column field="source" header="Fuente" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getSourceBadgeClass(data.source)">
              {{ data.source?.toUpperCase() || 'WEB' }}
            </span>
          </template>
        </Column>

        <Column field="user.name" header="Usuario" sortable>
          <template #body="{ data }">
            <div v-if="data.user" class="flex items-center">
              <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <span class="text-xs font-medium text-gray-600">{{ data.user.name.charAt(0).toUpperCase() }}</span>
              </div>
              <div class="ml-3">
                <div class="text-sm font-medium text-gray-900">{{ data.user.name }}</div>
              </div>
            </div>
            <span v-else class="text-sm text-gray-500">Sistema</span>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha" sortable>
          <template #body="{ data }">
            <div class="text-sm text-gray-900">{{ formatDate(data.createdAt) }}</div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { activityService } from '@/services/activityService'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dropdown from 'primevue/dropdown'

// Reactive data
const activities = ref([])
const loading = ref(false)
const selectedEntity = ref(null)
const selectedAction = ref(null)
const selectedSource = ref(null)

// Filter options
const entityOptions = [
  { label: 'Usuarios', value: 'user' },
  { label: 'Mascotas', value: 'pet' },
  { label: 'Razas', value: 'Breed' },
  { label: 'Tipos de Animal', value: 'AnimalType' },
  { label: 'Nutrición', value: 'Nutrition' },
  { label: 'Salud', value: 'Health' },
  { label: 'Educación', value: 'Education' }
]

const actionOptions = [
  { label: 'Creado', value: 'created' },
  { label: 'Actualizado', value: 'updated' },
  { label: 'Eliminado', value: 'deleted' }
]

const sourceOptions = [
  { label: 'Web', value: 'web' },
  { label: 'API', value: 'api' }
]

// Computed properties
const filteredActivities = computed(() => {
  if (!activities.value) return []
  
  return activities.value.filter(activity => {
    const entityMatch = !selectedEntity.value || activity.entity === selectedEntity.value
    const actionMatch = !selectedAction.value || activity.action === selectedAction.value
    const sourceMatch = !selectedSource.value || activity.source === selectedSource.value
    
    return entityMatch && actionMatch && sourceMatch
  })
})

const createdCount = computed(() => {
  return filteredActivities.value?.filter(a => a.action === 'created').length || 0
})

const updatedCount = computed(() => {
  return filteredActivities.value?.filter(a => a.action === 'updated').length || 0
})

const deletedCount = computed(() => {
  return filteredActivities.value?.filter(a => a.action === 'deleted').length || 0
})

// Methods
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const translateEntity = (entity) => {
  const translations = {
    'user': 'Usuario',
    'pet': 'Mascota',
    'Breed': 'Raza',
    'AnimalType': 'Tipo de Animal',
    'Nutrition': 'Nutrición',
    'Health': 'Salud',
    'Education': 'Educación'
  }
  return translations[entity] || entity
}



const translateAction = (action) => {
  const translations = {
    'created': 'Creado',
    'updated': 'Actualizado',
    'deleted': 'Eliminado'
  }
  return translations[action] || action
}

const getActivityIcon = (action) => {
  switch (action) {
    case 'created':
      return 'M12 6v6m0 0v6m0-6h6m-6 0H6'
    case 'updated':
      return 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
    case 'deleted':
      return 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
    default:
      return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
  }
}

const getActivityIconClass = (action) => {
  switch (action) {
    case 'created':
      return { iconBg: 'bg-green-100', iconColor: 'text-green-600' }
    case 'updated':
      return { iconBg: 'bg-blue-100', iconColor: 'text-blue-600' }
    case 'deleted':
      return { iconBg: 'bg-red-100', iconColor: 'text-red-600' }
    default:
      return { iconBg: 'bg-gray-100', iconColor: 'text-gray-600' }
  }
}

const getActionBadgeClass = (action) => {
  switch (action) {
    case 'created':
      return 'bg-green-100 text-green-800'
    case 'updated':
      return 'bg-blue-100 text-blue-800'
    case 'deleted':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getEntityBadgeClass = (entity) => {
  switch (entity) {
    case 'user':
      return 'bg-purple-100 text-purple-800'
    case 'pet':
      return 'bg-indigo-100 text-indigo-800'
    case 'breed':
      return 'bg-blue-100 text-blue-800'
    case 'AnimalType':
      return 'bg-cyan-100 text-cyan-800'
    case 'Nutrition':
      return 'bg-green-100 text-green-800'
    case 'Health':
      return 'bg-red-100 text-red-800'
    case 'Education':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getSourceBadgeClass = (source) => {
  switch (source) {
    case 'api':
      return 'bg-purple-100 text-purple-800'
    case 'web':
    default:
      return 'bg-indigo-100 text-indigo-800'
  }
}

const loadActivities = async () => {
  try {
    loading.value = true
    const response = await activityService.getAllActivities()
    activities.value = response.data || []
  } catch (error) {
    console.error('Error loading activities:', error)
    activities.value = []
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadActivities()
})
</script>

<style scoped>
.p-dropdown-custom {
  @apply border-gray-300 rounded-lg;
}
</style>